import { useCallback, useEffect, useRef, useState } from "react";
import {
  approveExecution,
  cancelExecution,
  ExecutionRow,
  getExecution,
  retryExecution,
  streamExecution,
} from "../lib/api";

/**
 * One inline execution card.
 *
 * Three states the reviewer interacts with:
 *
 * 1. pending_approval — Show Approve / Reject + cost & budget summary.
 *    Approve flips status server-side and immediately opens the SSE stream.
 * 2. approved | dispatching | running — Live stdout from SSE; Cancel button.
 * 3. completed | failed | cancelled — Terminal view: exit code, last stdout.
 *
 * Designed to live INSIDE the chat transcript (rendered by the tool chip
 * that proposed this execution) — see ToolChip.tsx for the integration.
 */
export default function ExecutionPanel(props: {
  sessionId: number;
  executionId: number;
}) {
  const { sessionId, executionId } = props;
  const [row, setRow] = useState<ExecutionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stdout, setStdout] = useState<string>("");
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Pull the latest row whenever the id changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getExecution(sessionId, executionId);
        if (!cancelled) setRow(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, executionId]);

  const openStream = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStreaming(true);
    try {
      for await (const ev of streamExecution(sessionId, executionId, ctrl.signal)) {
        if (ev.event === "stdout") {
          setStdout((s) => s + ev.data.chunk);
        } else if (ev.event === "status") {
          // Re-pull the row to sync the status field + the budget.
          const r = await getExecution(sessionId, executionId);
          setRow(r);
        } else if (ev.event === "done") {
          const r = await getExecution(sessionId, executionId);
          setRow(r);
        } else if (ev.event === "error") {
          setError(ev.data.message);
        }
      }
    } catch (e) {
      // AbortError is the normal lifecycle event when the panel re-runs
      // openStream (status change, parent re-render, unmount). The dispatcher
      // continues server-side regardless. Surfacing it as a red toast was
      // a bug that misled the operator into thinking executions had failed.
      if (e instanceof DOMException && e.name === "AbortError") return;
      // Also catch the streaming-layer wrappers some browsers emit
      // ("BodyStreamBuffer was aborted", "AbortError: BodyStream aborted").
      if (e instanceof TypeError && /aborted/i.test(e.message)) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStreaming(false);
    }
  }, [sessionId, executionId]);

  // Auto-open the stream as soon as the row goes into a streamable state.
  useEffect(() => {
    if (!row) return;
    if (
      ["approved", "dispatching", "running"].includes(row.status) &&
      !streaming
    ) {
      void openStream();
    }
    return () => {
      abortRef.current?.abort();
    };
    // openStream is stable across re-renders; row.status drives this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.status]);

  const onApprove = useCallback(async () => {
    if (!row) return;
    try {
      const r = await approveExecution(sessionId, executionId);
      setRow(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [row, sessionId, executionId]);

  const onCancel = useCallback(async () => {
    if (!row) return;
    try {
      const r = await cancelExecution(sessionId, executionId);
      setRow(r);
      abortRef.current?.abort();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [row, sessionId, executionId]);

  const onRetry = useCallback(async () => {
    if (!row) return;
    try {
      // The new execution gets a fresh row — open the SPA's URL to it
      // implicitly by navigating to the chat (just reload it visually).
      // The Approve panel for the NEW execution will appear after the
      // user types anything in chat (or the executions list refresh).
      const r = await retryExecution(sessionId, executionId);
      // Show a quick confirmation; the panel for the new row will surface
      // through the ChatBox executions refresh on the next chat turn.
      setRow({ ...row, error: `Retry queued as execution #${r.id} — approve it in the chat.` });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [row, sessionId, executionId]);

  if (error)
    return (
      <div className="my-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-900">
        Execution error: {error}
      </div>
    );
  if (!row)
    return (
      <div className="my-2 rounded border border-ink-200 bg-ink-50 p-2 text-xs text-ink-500">
        Loading execution #{executionId}…
      </div>
    );

  return (
    <div className="my-2 rounded border border-ink-200 bg-white text-xs">
      <div className="flex items-center justify-between border-b border-ink-200 px-2 py-1">
        <div>
          <code className="font-mono">run_on_aws</code>{" "}
          <span className="text-ink-500">·</span>{" "}
          <span className="font-medium">{row.profile}</span>{" "}
          <span className="text-ink-500">
            · est ${(row.estimated_cost_cents / 100).toFixed(2)} ·{" "}
            {row.estimated_wall_minutes} min cap
          </span>
        </div>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${statusColor(row.status)}`}
        >
          {STATUS_LABELS[row.status]}
        </span>
      </div>

      <div className="px-2 py-2">
        <pre className="overflow-auto whitespace-pre-wrap break-all rounded bg-ink-50 p-1.5 text-[11px] text-ink-900">
          {row.command}
        </pre>
        {row.expect_output_globs.length > 0 && (
          <div className="mt-1 text-[10px] text-ink-500">
            Will harvest: {row.expect_output_globs.join(", ")}
          </div>
        )}
        <BudgetRow budget={row.budget} />
      </div>

      {row.status === "pending_approval" && (
        <div className="flex items-center justify-end gap-2 border-t border-ink-200 px-2 py-1">
          <button
            onClick={onCancel}
            className="rounded px-2 py-0.5 text-ink-700 hover:bg-ink-50"
          >
            Reject
          </button>
          <button
            onClick={onApprove}
            disabled={!row.budget.ok}
            className="rounded bg-ink-700 px-2 py-0.5 text-white hover:bg-ink-900 disabled:bg-ink-300"
          >
            Approve (${(row.estimated_cost_cents / 100).toFixed(2)})
          </button>
        </div>
      )}

      {["approved", "dispatching", "running"].includes(row.status) && (
        <div className="border-t border-ink-200">
          <div className="flex items-center justify-between px-2 py-1 text-[10px] text-ink-500">
            <span>{streaming ? "Streaming…" : "Idle"}</span>
            <button
              onClick={onCancel}
              className="rounded px-2 py-0.5 text-ink-700 hover:bg-ink-50"
            >
              Cancel
            </button>
          </div>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap bg-ink-900 px-2 py-1 text-[11px] text-ink-100">
            {stdout || "(waiting for stdout…)"}
          </pre>
        </div>
      )}

      {["completed", "failed", "cancelled"].includes(row.status) && (
        <div className="border-t border-ink-200 px-2 py-1">
          <div className="text-[11px]">
            {row.status === "completed"
              ? `✓ exit 0${
                  row.actual_wall_seconds != null
                    ? `, ${row.actual_wall_seconds}s wall, ${
                        row.actual_cost_cents != null
                          ? `$${(row.actual_cost_cents / 100).toFixed(2)}`
                          : ""
                      }`
                    : ""
                }`
              : row.status === "failed"
                ? `✗ exit ${row.exit_code ?? "?"}${row.error ? ` — ${row.error}` : ""}`
                : "Cancelled."}
          </div>
          {row.stdout_tail && (
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap bg-ink-900 px-2 py-1 text-[11px] text-ink-100">
              {row.stdout_tail}
            </pre>
          )}
          {(row.status === "failed" || row.status === "cancelled") && (
            <div className="mt-2 flex justify-end">
              <button
                onClick={onRetry}
                className="rounded bg-ink-700 px-2 py-0.5 text-xs text-white hover:bg-ink-900"
                title="Create a new execution with the same command, profile, and wall budget"
              >
                ↻ Retry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BudgetRow(props: { budget: ExecutionRow["budget"] }) {
  const b = props.budget;
  return (
    <div
      className={`mt-1 rounded p-1 text-[10px] ${b.ok ? "bg-ink-50 text-ink-500" : "bg-red-50 text-red-900"}`}
    >
      {b.ok ? (
        <>
          Session spent ${(b.session_committed_cents / 100).toFixed(2)} of $
          {(b.per_session_cap_cents / 100).toFixed(2)} cap · 30-day total $
          {(b.user_committed_cents / 100).toFixed(2)} of $
          {(b.per_user_month_cap_cents / 100).toFixed(2)}.
        </>
      ) : (
        <>Budget block: {b.reason}</>
      )}
    </div>
  );
}

const STATUS_LABELS: Record<ExecutionRow["status"], string> = {
  pending_approval: "Awaiting approval",
  approved: "Approved",
  dispatching: "Starting instance",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

function statusColor(status: ExecutionRow["status"]): string {
  switch (status) {
    case "pending_approval":
      return "bg-yellow-100 text-yellow-900";
    case "approved":
    case "dispatching":
    case "running":
      return "bg-blue-100 text-blue-900";
    case "completed":
      return "bg-green-100 text-green-900";
    case "failed":
    case "cancelled":
      return "bg-red-100 text-red-900";
  }
}
